import type {
  CertificateAiWorkflow,
  Certification,
  CertificationStandard,
  CertificationStylesPage,
  CertificationsResponse,
  UpdateCertificationPayload,
} from '@/api/certifications/types';
import type { CreateCertificationPayload } from '@/api/network/types';
import { API_BASE_URL, BACKEND_URL } from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';

export async function fetchCertifications(
  qs: string,
): Promise<CertificationsResponse> {
  return apiFetch<CertificationsResponse>(`/v3/claims?${qs}`, {}, API_BASE_URL);
}

export async function bulkArchiveCertifications(ids: string[]): Promise<void> {
  await apiFetch<void>(
    '/v3/claims/bulk-archive',
    { method: 'PATCH', body: JSON.stringify({ ids }) },
    API_BASE_URL,
  );
}

export async function downloadCertifications(ids: string[]): Promise<void> {
  await apiFetch<void>(
    '/company-certifications/download',
    { method: 'POST', body: JSON.stringify(ids) },
    BACKEND_URL,
  );
}

export async function exportCertifications(ids: string[]): Promise<void> {
  await apiFetch<void>(
    '/claims/export',
    { method: 'POST', body: JSON.stringify({ ids }) },
    BACKEND_URL,
  );
}

export async function fetchCertificationFileUrl(
  fileUrl: string,
): Promise<{ signedUrl: string }> {
  return apiFetch<{ signedUrl: string }>(
    `/v3/files/${fileUrl}`,
    {},
    API_BASE_URL,
  );
}

export async function triggerCertificateAiWorkflow(
  fileId: string,
): Promise<{ data: CertificateAiWorkflow }> {
  return apiFetch<{ data: CertificateAiWorkflow }>(
    `/certificate-ai/${fileId}`,
    { method: 'POST', body: JSON.stringify({}) },
    BACKEND_URL,
  );
}

export async function getCertificateAiWorkflow(
  workflowId: string,
): Promise<{ data: CertificateAiWorkflow }> {
  return apiFetch<{ data: CertificateAiWorkflow }>(
    `/certificate-ai/${workflowId}`,
    {},
    BACKEND_URL,
  );
}

export async function cancelCertificateAiWorkflow(
  workflowId: string,
): Promise<void> {
  await apiFetch<void>(
    '/certificate-ai/cancel',
    { method: 'DELETE', body: JSON.stringify({ workflowId }) },
    BACKEND_URL,
  );
}

export async function fetchCertificationStandards(): Promise<{
  data: CertificationStandard[];
}> {
  return apiFetch<{ data: CertificationStandard[] }>(
    '/v3/certifications?isDropDown=true',
    {},
    API_BASE_URL,
  );
}

export async function fetchCertificationById(
  id: string,
): Promise<Certification> {
  return apiFetch<Certification>(`/v3/claims/${id}`, {}, API_BASE_URL);
}

export async function fetchCertificationVersionHistory(
  id: string,
  qs: string,
): Promise<CertificationsResponse> {
  return apiFetch<CertificationsResponse>(
    `/v3/claims/${id}/list-expired-versions?${qs}`,
    {},
    API_BASE_URL,
  );
}

export async function createCertification(
  payload: CreateCertificationPayload,
): Promise<{ data: { id: string } }> {
  return apiFetch<{ data: { id: string } }>(
    '/claims',
    { method: 'POST', body: JSON.stringify(payload) },
    BACKEND_URL,
  );
}

export async function updateCertification(
  id: string,
  payload: UpdateCertificationPayload,
): Promise<void> {
  await apiFetch<void>(
    `/claims/${id}`,
    { method: 'PUT', body: JSON.stringify(payload) },
    BACKEND_URL,
  );
}

export async function submitCertificationForReview(id: string): Promise<void> {
  await apiFetch<void>(
    `/v3/claims/${id}/request`,
    { method: 'PUT', body: JSON.stringify({}) },
    API_BASE_URL,
  );
}

export async function fetchCertificationLinkedStyles(
  id: string,
  qs: string,
): Promise<CertificationStylesPage> {
  return apiFetch<CertificationStylesPage>(
    `/claims/${id}/list-styles?${qs}`,
    {},
    BACKEND_URL,
  );
}

export function fetchStylesList(
  body: object,
): Promise<CertificationStylesPage> {
  return apiFetch(
    '/styles/list',
    { method: 'POST', body: JSON.stringify(body) },
    BACKEND_URL,
  );
}

export function fetchVariantsList(
  body: object,
): Promise<CertificationStylesPage> {
  return apiFetch(
    '/style-variants/list',
    { method: 'POST', body: JSON.stringify(body) },
    BACKEND_URL,
  );
}

export function fetchSkusList(body: object): Promise<CertificationStylesPage> {
  return apiFetch(
    '/variant-skus/list',
    { method: 'POST', body: JSON.stringify(body) },
    BACKEND_URL,
  );
}

export async function validateCertificationStandard(
  standardId: string,
  companyId: string,
): Promise<
  Array<{
    validationResult: {
      valid: boolean;
      description: Array<Array<{ type: string; text: string }>>;
    };
  }>
> {
  return apiFetch(
    '/v3/certifications/validate',
    {
      method: 'POST',
      body: JSON.stringify([{ certificateId: standardId, companyId }]),
    },
    API_BASE_URL,
  );
}

export async function fetchExistingCertificates(
  standardId: string,
  companyId: string,
): Promise<{
  data: Array<{ id: string; name: string; documentNumber?: string | null }>;
}> {
  return apiFetch<{
    data: Array<{ id: string; name: string; documentNumber?: string | null }>;
  }>(
    `/v3/claims?isDropDown=true&standardId=${encodeURIComponent(standardId)}&companyId=${encodeURIComponent(companyId)}`,
    { method: 'GET' },
    API_BASE_URL,
  );
}

export async function deleteCertification(id: string): Promise<void> {
  await apiFetch<void>(`/v3/claims/${id}`, { method: 'DELETE' }, API_BASE_URL);
}

export async function archiveCertification(id: string): Promise<void> {
  await apiFetch<void>(
    `/v3/claims/${id}/to-archive`,
    { method: 'PUT', body: JSON.stringify({}) },
    API_BASE_URL,
  );
}

export async function unarchiveCertification(id: string): Promise<void> {
  await apiFetch<void>(
    `/v3/claims/${id}/to-unarchive`,
    { method: 'PUT', body: JSON.stringify({}) },
    API_BASE_URL,
  );
}
